import React, { useState, useEffect, useRef } from 'react';
import { Clock } from 'lucide-react';
import { formatCurrency, formatDuration, formatDurationLong } from '../utils/formatters';
import { calculateCostPerMinute, calculateHourlyRate } from '../utils/calculations';
import { Comparisons } from '../utils/comparisons';

export default function TrackerPage({ participants, navigateTo, currency }) {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const startTimeRef = useRef(Date.now());
  const intervalRef = useRef(null);
  const isEndingRef = useRef(false);

  const costPerMinute = calculateCostPerMinute(participants);
  const currentCost = (costPerMinute * elapsedSeconds) / 60;

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      if (isEndingRef.current) return;
      
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setElapsedSeconds(elapsed);
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [costPerMinute]);

  const getBackgroundColor = () => {
    const cost = currentCost;
    if (cost < 200) return 'from-green-400 to-green-500';
    if (cost < 500) return 'from-yellow-400 to-yellow-500';
    if (cost < 1000) return 'from-orange-400 to-orange-500';
    return 'from-red-400 to-red-500';
  };

  const handleEndMeetingClick = () => {
    setShowEndConfirm(true);
  };

  const confirmEndMeeting = () => {
    if (isEndingRef.current) return;
    
    isEndingRef.current = true;
    setShowEndConfirm(false);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    const finalDuration = Math.round(elapsedSeconds / 60);
    const finalCost = currentCost;
    const finalCostPerMinute = costPerMinute;

    const finalMeetingData = {
      duration: finalDuration,
      durationSeconds: elapsedSeconds,
      participants: participants.length,
      participantDetails: participants,
      totalCost: finalCost,
      costPerMinute: finalCostPerMinute,
      comparison: Comparisons.getComparison(finalCost),
      visualComparisons: Comparisons.getVisualComparisons(finalCost),
      currency: currency
    };
    
    navigateTo('results', finalMeetingData);
  };

  const cancelEndMeeting = () => {
    setShowEndConfirm(false);
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${getBackgroundColor()} flex flex-col items-center justify-center p-4 transition-all duration-1000`}>
      <div className="absolute top-8 right-8 text-white">
        <div className="flex items-center text-2xl font-bold bg-black bg-opacity-20 px-4 py-2 rounded-lg">
          <Clock className="w-6 h-6 mr-2" />
          {formatDuration(elapsedSeconds)}
        </div>
      </div>

      <div className="text-center mb-12">
        <div className="text-8xl md:text-9xl font-extrabold text-white mb-4 drop-shadow-lg">
          {formatCurrency(currentCost, currency)}
        </div>
        <div className="text-2xl text-white font-medium drop-shadow">
          {formatCurrency(costPerMinute, currency)}/minute
        </div>
      </div>

      <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-6 mb-8">
        {/* Check if Quick Mode */}
        {participants.length > 0 && participants[0].isQuickMode ? (
          // Quick Mode: Show summary
          <div className="text-center text-white">
            <div className="flex items-center justify-center gap-4 mb-3">
              <div className="text-5xl">👥</div>
              <div>
                <div className="text-3xl font-bold">{participants.length} Participants</div>
                <div className="text-lg opacity-90">Average Salary Mode</div>
              </div>
            </div>
            <div className="text-xl font-medium mt-2">
              {formatCurrency(calculateHourlyRate(participants[0].salaryValue, participants[0].salaryType) / 60, currency)}/min per person
            </div>
          </div>
        ) : (
          // Detailed Mode: Show individual participants
          <div className="flex items-center gap-4 flex-wrap justify-center">
            {participants.map((p, i) => (
              <div key={i} className="text-white text-center">
                <div className="text-4xl mb-1">👤</div>
                <div className="font-medium">{p.name || `Person ${i + 1}`}</div>
                <div className="text-sm opacity-90">
                  {formatCurrency(calculateHourlyRate(p.salaryValue, p.salaryType) / 60, currency)}/min
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showEndConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">End this meeting?</h3>
            <p className="text-gray-600 mb-6">
              The meeting has lasted {formatDurationLong(elapsedSeconds)} and cost {formatCurrency(currentCost, currency)}.
            </p>
            <div className="flex gap-4">
              <button
                onClick={cancelEndMeeting}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmEndMeeting}
                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700"
              >
                End Meeting
              </button>
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={handleEndMeetingClick}
        className="bg-white text-gray-900 px-12 py-4 rounded-lg text-xl font-bold hover:bg-gray-100 shadow-lg transform hover:scale-105 transition-all cursor-pointer"
      >
        End Meeting
      </button>
    </div>
  );
}